import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import {
  ID_ESTADO_PERCEPCIONES_OK,
  ID_ESTADO_PERCEPCIONES_NO_OK
} from '../../../constants/pedidoEstados'
import { formatearDireccion } from '../../helpers/helper'
import LinkButton from '../../ui/LinkButton'
import Precios from '../precios/Precios'
import EnvioItem from './EnvioItem'
import styles from './Pedido.module.css'
import { PedidoItem } from './PedidoItem'

const PAGO_PENDIENTE = 0
const PAGO_EN_CTA_CTE = 3
const PAGO_OK = 1
const PAGO_RECHAZADO = 2
const MAX_CARACTERES_LINEA = 35
const PAGO_ANTICIPO = 7

export const Pedido = ({ pedido, esVentas, esAdministrador }) => {
  const { pedidoItems, idComprador, idVendedor } = pedido
  const fechaPedido = pedidoItems[0]?.fechaPedido || pedido.fechaPedido
  const {
    monedaSimbolo,
    idPublicacion,
    idPedidoItem,
    idPedido,
    vendedorNombre,
    vendedorEMail,
    vendedorTelefono,
    vendedorCalle,
    vendedorCalleAltura,
    vendedorPiso,
    vendedorCP,
    estadoPago,
    idPagoEstado,
    razonSocial,
    numPedEBP
  } = pedidoItems[0]
  const vendedorDireccion = formatearDireccion(
    vendedorCalle,
    vendedorCalleAltura,
    vendedorPiso,
    vendedorCP
  )
  const direccionFormateada = (vendedorDireccion ?? '').replace(/[,\s]/g, '')
  const pathCalificarVendedor = `/CalificarVendedor/${idPublicacion}/${idVendedor}/${idPedidoItem}`

  const costoEnvioTotal = (() => {
    const preciosUnicos = new Set()

    pedidoItems.forEach((item) => {
      const precio = Number(item.precioEnvio) || 0
      if (precio > 0) {
        preciosUnicos.add(precio)
      }
    })

    return preciosUnicos.size > 0 ? Math.max(...Array.from(preciosUnicos)) : 0
  })()

  const [items, setItems] = useState(pedidoItems)
  useEffect(() => {
    setItems(pedidoItems)
  }, [pedidoItems])

  const renderDescripcionEstado = (estado, clase) => {
    if (!estado) {
      return null
    }

    const texto = estado.includes('\n')
      ? estado
      : estado.length > MAX_CARACTERES_LINEA
        ? `${estado.slice(0, MAX_CARACTERES_LINEA)}\n${estado.slice(MAX_CARACTERES_LINEA)}`
        : estado

    return (
      <span className={clase} style={{ whiteSpace: 'pre-line' }}>
        {texto}
      </span>
    )
  }

  const itemPendientePercepciones = items.find(
    (item) =>
      item.idPedidoItemEstado === ID_ESTADO_PERCEPCIONES_OK ||
      item.idPedidoItemEstado === ID_ESTADO_PERCEPCIONES_NO_OK
  )

  const itemPercepcionOK = items.find(
    (item) => item.idPedidoItemEstado === ID_ESTADO_PERCEPCIONES_OK
  )
  const itemPercepcionNoOK = items.find(
    (item) => item.idPedidoItemEstado === ID_ESTADO_PERCEPCIONES_NO_OK
  )

  const vendedorYaCalificado = items.some((item) => item.esVendedorCalificado === 1)

  return (
    <div className={styles.pedido}>
      <div className={styles.cabecera}>
        <h3 className={styles.titulo}>
          Pedido Nº {idPedido}
          {esVentas && numPedEBP && <span className={styles.nroErp}> | OC: {numPedEBP}</span>}
        </h3>

        <div>
          <Precios
            items={items}
            showSinIVA={true}
            monedaSimbolo={monedaSimbolo}
            costoEnvio={costoEnvioTotal}
          />
        </div>
      </div>
      <div className={styles.items}>
        {items.map((item, index) => (
          <PedidoItem
            key={item.idPedidoItem}
            pedidoItem={{ ...item, idComprador, idVendedor }}
            fechaPedido={fechaPedido}
            isFirstItem={index === 0}
            esVentas={esVentas}
            tienePercepcionesPendientes={Boolean(itemPendientePercepciones)}
            esAdministrador={esAdministrador}
          />
        ))}
        {costoEnvioTotal > 0 && <EnvioItem costoEnvio={costoEnvioTotal} />}
      </div>
      <div className={styles.infoVendedor}>
        <div className={styles.pedidoDescripcion}>
          <div
            className={`
              ${styles.estado}
              ${
                idPagoEstado === PAGO_PENDIENTE || idPagoEstado === PAGO_EN_CTA_CTE
                  ? styles.estadoPendiente
                  : idPagoEstado === PAGO_OK
                    ? styles.estadoOK
                    : idPagoEstado === PAGO_RECHAZADO
                      ? styles.estadoRechazado
                      : idPagoEstado === PAGO_ANTICIPO
                        ? styles.estadoPagoAnticipo
                        : styles.estadoDefault
              }
            `}
          >
            <strong>Estado del pago: </strong> {estadoPago}
          </div>
          <span>
            <i className="fa fa-user bigger-icon mr-2" aria-hidden="true" />
            {vendedorNombre} {razonSocial && ` - ${razonSocial}`}
          </span>
          {vendedorTelefono && vendedorTelefono.trim() !== '' && (
            <span>
              <i className="fa fa-phone bigger-icon mr-2" aria-hidden="true" />
              {vendedorTelefono.trim()}
            </span>
          )}
          <span>
            <i className="fa fa-at bigger-icon mr-2" aria-hidden="true" />
            {vendedorEMail}
          </span>
          {direccionFormateada !== '' && (
            <span>
              <i className="fa fa-home bigger-icon mr-2" aria-hidden="true" />
              {vendedorDireccion}
            </span>
          )}
        </div>
        <div className={styles.items}>
          {esVentas &&
            (itemPercepcionOK
              ? renderDescripcionEstado(itemPercepcionOK?.descripcionEstado, styles.estadoOk)
              : itemPercepcionNoOK
                ? renderDescripcionEstado(itemPercepcionNoOK?.descripcionEstado, styles.estadoNoOk)
                : null)}
          <div className={styles.items}>
            {idPagoEstado !== PAGO_OK && (
              <div className={styles.buttonSection}>
                {esVentas && !esAdministrador && items.length > 1 && (
                  <LinkButton
                    to={{
                      pathname: `/gestionEnvio/${idPedido}`,
                      state: { pedido }
                    }}
                    className={styles.califButton}
                  >
                    <i className="fa fa-file-text-o" aria-hidden="true" />
                    Gestionar envio
                  </LinkButton>
                )}
                {!esAdministrador && (
                  <LinkButton
                    to={{
                      pathname: `/Pedido/${idPedido}`,
                      state: { pedido, esVentas }
                    }}
                    className={styles.califButton}
                  >
                    <i className="fa fa-file-text-o" aria-hidden="true" />
                    Gestionar pedido
                  </LinkButton>
                )}
              </div>
            )}
            {!esVentas && !esAdministrador && (
              <>
                <LinkButton to={`/AnalisisCompra/${idPedido}`} className={styles.califButton}>
                  <i className="fa fa-bar-chart" aria-hidden="true" />
                  Análisis de compra
                </LinkButton>
                <LinkButton
                  to={pathCalificarVendedor}
                  disabled={vendedorYaCalificado}
                  className={`
                ${vendedorYaCalificado ? styles.disabledButton : ''}
                ${styles.califButton}
              `}
                >
                  <i className="fa fa-star" aria-hidden="true" />
                  Calificar Vendedor
                </LinkButton>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

Pedido.propTypes = {
  pedido: PropTypes.shape({
    pedidoItems: PropTypes.arrayOf(
      PropTypes.shape({
        monedaSimbolo: PropTypes.string.isRequired,
        idPublicacion: PropTypes.number.isRequired,
        idPedidoItem: PropTypes.number.isRequired,
        idPedido: PropTypes.number.isRequired,
        vendedorNombre: PropTypes.string.isRequired,
        vendedorEMail: PropTypes.string.isRequired,
        vendedorTelefono: PropTypes.string.isRequired,
        esVendedorCalificado: PropTypes.number.isRequired,
        vendedorCalle: PropTypes.string,
        vendedorCalleAltura: PropTypes.string,
        vendedorPiso: PropTypes.string,
        vendedorCP: PropTypes.string,
        precioUnitario: PropTypes.number.isRequired,
        cantidad: PropTypes.number,
        alicuota: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        fechaPedido: PropTypes.string,
        medioPago: PropTypes.string,
        estadoPago: PropTypes.string,
        idPagoEstado: PropTypes.number,
        razonSocial: PropTypes.string,
        descripcionEstado: PropTypes.string,
        costoEnvio: PropTypes.number,
        idPedidoItemEstado: PropTypes.number,
        numPedEBP: PropTypes.string
      })
    ).isRequired,
    idPedido: PropTypes.number.isRequired,
    fechaPedido: PropTypes.string.isRequired,
    idComprador: PropTypes.number,
    idVendedor: PropTypes.number
  }).isRequired,
  esVentas: PropTypes.bool.isRequired,
  esAdministrador: PropTypes.bool.isRequired
}
